import React, { useEffect, useState } from "react";
import { Link as ReactRouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, CheckCircle2, XCircle, Clock, Mail, Phone, Building2, Calendar, Link2, UserPlus, Trash2, KeyRound, Copy, Check, Coins } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { toast } from "sonner";

const ROLE_TABS = [
    { key: "agent", label: "Makelaars", accent: "#E5A93D" },
    { key: "marketing_manager", label: "Marketing Managers", accent: "#7C3AED" },
    { key: "photographer", label: "Fotografen", accent: "#0EA5E9" },
];
const STATUS_TABS = ["pending", "approved", "rejected"];
const STATUS_LABELS = { pending: "In afwachting", approved: "Goedgekeurd", rejected: "Geweigerd" };

export default function AdminUsers() {
    const [roleTab, setRoleTab] = useState("agent");
    const [statusTab, setStatusTab] = useState("pending");
    const [users, setUsers] = useState([]);
    const [allAgents, setAllAgents] = useState([]); // approved agents for linking
    const [counts, setCounts] = useState({ agent: { pending: 0, approved: 0, rejected: 0 }, marketing_manager: { pending: 0, approved: 0, rejected: 0 }, photographer: { pending: 0, approved: 0, rejected: 0 } });
    const [loading, setLoading] = useState(true);
    const [acting, setActing] = useState(null);
    const [linkingFor, setLinkingFor] = useState(null); // user id being edited
    const [linkSelection, setLinkSelection] = useState([]);
    const [savingLink, setSavingLink] = useState(false);
    const [deleteFor, setDeleteFor] = useState(null); // user object pending deletion
    const [deleting, setDeleting] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createOpen, setCreateOpen] = useState(false);
    const [createForm, setCreateForm] = useState({
        account_type: "agent",
        name: "",
        email: "",
        password: "",
        company: "",
        phone: "",
        marketing_email: "",
        logo_url: "",
    });
    const [logoUploading, setLogoUploading] = useState(false);
    const [resetLink, setResetLink] = useState(null); // { url, user_email, user_name, expires_at }
    const [resetCopied, setResetCopied] = useState(false);

    const generateResetLink = async (u) => {
        setActing(u.id);
        try {
            const { data } = await api.post(`/admin/users/${u.id}/reset-password-link`);
            const url = `${window.location.origin}/reset-password?token=${encodeURIComponent(data.token)}`;
            setResetLink({ url, user_email: data.user_email, user_name: data.user_name, expires_at: data.expires_at, ttl_hours: data.ttl_hours });
            setResetCopied(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon reset link niet aanmaken");
        }
        setActing(null);
    };

    const copyResetLink = async () => {
        if (!resetLink) return;
        try {
            await navigator.clipboard.writeText(resetLink.url);
            setResetCopied(true);
            toast.success("Link gekopieerd — deel via WhatsApp/SMS");
            setTimeout(() => setResetCopied(false), 2500);
        } catch {
            toast.error("Kopiëren mislukt — selecteer en kopieer manueel");
        }
    };

    const updateCreate = (k, v) => setCreateForm((f) => ({ ...f, [k]: v }));

    const generatePassword = () => {
        const chars = "abcdefghijkmnpqrstuvwxyz23456789ABCDEFGHJKMNPQRSTUVWXYZ!@#$";
        let p = "";
        for (let i = 0; i < 12; i++) p += chars[Math.floor(Math.random() * chars.length)];
        updateCreate("password", p);
    };

    const onLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setLogoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            updateCreate("logo_url", data.url);
            toast.success("Logo geüpload");
        } catch (e) { toast.error("Upload mislukt"); }
        setLogoUploading(false);
    };

    const submitCreate = async () => {
        if (!createForm.name.trim() || !createForm.email.trim() || !createForm.password.trim()) {
            return toast.error("Naam, e-mail en paswoord zijn verplicht");
        }
        if (createForm.password.length < 6) return toast.error("Paswoord moet minstens 6 tekens lang zijn");
        setCreating(true);
        try {
            await api.post("/admin/users/create", {
                ...createForm,
                company: createForm.company || null,
                phone: createForm.phone || null,
                marketing_email: createForm.marketing_email || null,
                logo_url: createForm.logo_url || null,
            });
            toast.success(`${createForm.name} aangemaakt en goedgekeurd`);
            setCreateOpen(false);
            setCreateForm({
                account_type: roleTab,
                name: "", email: "", password: "",
                company: "", phone: "", marketing_email: "", logo_url: "",
            });
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Aanmaken mislukt"); }
        setCreating(false);
    };

    const accent = roleTab === "agent" ? "#E5A93D" : "#7C3AED";

    const load = async () => {
        setLoading(true);
        try {
            const [
                aP, aA, aR, mP, mA, mR, pP, pA, pR, approvedAgents,
            ] = await Promise.all([
                api.get("/admin/users?account_type=agent&status=pending"),
                api.get("/admin/users?account_type=agent&status=approved"),
                api.get("/admin/users?account_type=agent&status=rejected"),
                api.get("/admin/users?account_type=marketing_manager&status=pending"),
                api.get("/admin/users?account_type=marketing_manager&status=approved"),
                api.get("/admin/users?account_type=marketing_manager&status=rejected"),
                api.get("/admin/users?account_type=photographer&status=pending"),
                api.get("/admin/users?account_type=photographer&status=approved"),
                api.get("/admin/users?account_type=photographer&status=rejected"),
                api.get("/admin/users?account_type=agent&status=approved"),
            ]);
            setCounts({
                agent: { pending: aP.data.length, approved: aA.data.length, rejected: aR.data.length },
                marketing_manager: { pending: mP.data.length, approved: mA.data.length, rejected: mR.data.length },
                photographer: { pending: pP.data.length, approved: pA.data.length, rejected: pR.data.length },
            });
            const map = {
                agent: { pending: aP.data, approved: aA.data, rejected: aR.data },
                marketing_manager: { pending: mP.data, approved: mA.data, rejected: mR.data },
                photographer: { pending: pP.data, approved: pA.data, rejected: pR.data },
            };
            setUsers(map[roleTab][statusTab]);
            setAllAgents(approvedAgents.data);
        } catch {
            toast.error("Kon gebruikers niet laden");
        }
        setLoading(false);
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [roleTab, statusTab]);

    const approve = async (id) => {
        setActing(id);
        try {
            await api.post(`/admin/users/${id}/approve`);
            toast.success("Account goedgekeurd");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
        setActing(null);
    };
    const reject = async (id) => {
        if (!window.confirm("Weet je zeker?")) return;
        setActing(id);
        try {
            await api.post(`/admin/users/${id}/reject`);
            toast.success("Account geweigerd");
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
        setActing(null);
    };

    const openLinkEditor = (u) => {
        setLinkingFor(u.id);
        setLinkSelection(u.managed_agent_ids || []);
    };
    const saveLink = async () => {
        setSavingLink(true);
        try {
            await api.post(`/admin/users/${linkingFor}/link-agents`, { agent_ids: linkSelection });
            toast.success("Makelaars gekoppeld");
            setLinkingFor(null);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Fout"); }
        setSavingLink(false);
    };

    const confirmDelete = async () => {
        if (!deleteFor) return;
        setDeleting(true);
        try {
            await api.delete(`/admin/users/${deleteFor.id}`);
            toast.success(`${deleteFor.name} verwijderd`);
            setDeleteFor(null);
            await load();
        } catch (e) { toast.error(e.response?.data?.detail || "Verwijderen mislukt"); }
        setDeleting(false);
    };

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-zinc-950 font-body pb-20" data-testid="admin-users-page">
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-10">
                <div className="text-xs uppercase tracking-[0.3em] font-semibold mb-3" style={{ color: accent }}>Admin · Gebruikers</div>
                <div className="flex items-center gap-3 mb-8">
                    <Users className="h-6 w-6" style={{ color: accent }} />
                    <h1 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-none">Accounts</h1>
                </div>

                {/* Account type selector + create */}
                <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                    <div className="inline-flex items-center gap-1 p-1 bg-[#FFFFFF] border border-zinc-100 rounded-sm" data-testid="role-tabs">
                        {ROLE_TABS.map((r) => {
                            const active = roleTab === r.key;
                            const total = (counts[r.key]?.pending || 0) + (counts[r.key]?.approved || 0) + (counts[r.key]?.rejected || 0);
                            return (
                                <button
                                    key={r.key}
                                    onClick={() => setRoleTab(r.key)}
                                    className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${active ? "text-black" : "text-zinc-500 hover:text-zinc-950 hover:bg-zinc-100"}`}
                                    style={active ? { backgroundColor: r.accent } : {}}
                                    data-testid={`role-tab-${r.key}`}
                                >
                                    {r.label} <span className="text-[10px] opacity-70 ml-1">({total})</span>
                                </button>
                            );
                        })}
                    </div>
                    <Button
                        onClick={() => { setCreateForm((f) => ({ ...f, account_type: roleTab })); setCreateOpen(true); }}
                        className="h-10 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]"
                        data-testid="open-create-user-btn"
                    >
                        <UserPlus className="h-4 w-4 mr-1.5" />
                        Maak nieuwe {roleTab === "agent" ? "makelaar" : roleTab === "photographer" ? "fotograaf" : "marketing manager"}
                    </Button>
                </div>

                {/* Status tabs */}
                <div className="flex flex-wrap items-center gap-2 mb-8 border-b border-zinc-100" data-testid="status-tabs">
                    {STATUS_TABS.map((t) => (
                        <button
                            key={t}
                            onClick={() => setStatusTab(t)}
                            className={`relative px-4 py-3 text-sm font-medium transition-colors ${statusTab === t ? "text-zinc-950" : "text-zinc-500 hover:text-zinc-600"}`}
                            data-testid={`status-tab-${t}`}
                        >
                            <span className="flex items-center gap-2">
                                {STATUS_LABELS[t]}
                                <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-full ${statusTab === t ? "text-black" : "bg-zinc-100 text-zinc-500"}`} style={statusTab === t ? { backgroundColor: accent } : {}}>
                                    {counts[roleTab]?.[t] || 0}
                                </span>
                            </span>
                            {statusTab === t && <span className="absolute left-0 right-0 bottom-[-1px] h-px" style={{ backgroundColor: accent }} />}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="text-zinc-500 py-20 text-center">Laden...</div>
                ) : users.length === 0 ? (
                    <div className="text-zinc-500 py-20 text-center border border-dashed border-zinc-200 rounded-md" data-testid="no-users-empty">
                        Geen {roleTab === "agent" ? "makelaars" : "marketing managers"} in deze categorie
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {users.map((u, i) => (
                            <motion.div
                                key={u.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.3, delay: i * 0.04 }}
                                className="bg-[#FFFFFF] border border-zinc-100 rounded-md p-5 flex flex-col gap-3"
                                data-testid={`user-card-${u.id}`}
                            >
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-center gap-3 min-w-0">
                                        {u.logo_url ? (
                                            <img src={u.logo_url.startsWith("http") ? u.logo_url : `${process.env.REACT_APP_BACKEND_URL}${u.logo_url}`} alt={u.name} className="h-10 w-10 rounded-sm object-contain bg-zinc-100" />
                                        ) : (
                                            <div className="h-10 w-10 rounded-sm border flex items-center justify-center text-sm font-bold" style={{ backgroundColor: accent + "20", borderColor: accent + "55", color: accent }}>
                                                {u.name?.charAt(0)?.toUpperCase()}
                                            </div>
                                        )}
                                        <div className="min-w-0">
                                            <div className="font-medium text-zinc-950 truncate">{u.name}</div>
                                            {u.company && <div className="text-xs text-zinc-500 truncate flex items-center gap-1"><Building2 className="h-3 w-3" />{u.company}</div>}
                                        </div>
                                    </div>
                                    <StatusPill status={u.approval_status} />
                                </div>

                                <div className="space-y-1.5 text-xs text-zinc-500 border-t border-zinc-100 pt-3">
                                    <div className="flex items-center gap-2"><Mail className="h-3 w-3 text-zinc-400" /><span className="truncate">{u.email}</span></div>
                                    {u.phone && <div className="flex items-center gap-2"><Phone className="h-3 w-3 text-zinc-400" />{u.phone}</div>}
                                    <div className="flex items-center gap-2 text-zinc-400"><Calendar className="h-3 w-3" />{new Date(u.created_at).toLocaleDateString("nl-BE")}</div>
                                </div>

                                {/* Marketing manager: linked agents + link button */}
                                {(u.account_type === "marketing_manager" || u.account_type === "photographer") && u.approval_status === "approved" && (
                                    <div className="border-t border-zinc-100 pt-3 space-y-2" data-testid={`mm-links-${u.id}`}>
                                        <div className="flex items-center justify-between">
                                            <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1">
                                                <Link2 className="h-3 w-3" /> Gekoppelde makelaars ({u.managed_agent_ids?.length || 0})
                                            </div>
                                            <button onClick={() => openLinkEditor(u)} className="text-[11px] text-[#7C3AED] hover:text-[#8B5CF6] inline-flex items-center gap-1" data-testid={`link-agents-btn-${u.id}`}>
                                                <UserPlus className="h-3 w-3" /> Beheren
                                            </button>
                                        </div>
                                        {u.managed_agent_ids?.length > 0 && (
                                            <div className="flex flex-wrap gap-1.5">
                                                {u.managed_agent_ids.map((aid) => {
                                                    const a = allAgents.find((x) => x.id === aid);
                                                    return (
                                                        <span key={aid} className="text-[10px] px-2 py-0.5 rounded-sm bg-[#7C3AED]/15 border border-[#7C3AED]/30 text-[#7C3AED]">
                                                            {a?.name || aid.slice(0, 6)}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {u.approval_status === "pending" && (
                                    <div className="flex items-center gap-2 pt-2 border-t border-zinc-100">
                                        <Button onClick={() => approve(u.id)} disabled={acting === u.id} className="flex-1 bg-emerald-500 hover:bg-emerald-400 text-black font-bold h-9 rounded-sm active:scale-[0.98]" data-testid={`approve-${u.id}`}>
                                            <CheckCircle2 className="h-4 w-4 mr-1.5" /> Goedkeuren
                                        </Button>
                                        <Button onClick={() => reject(u.id)} disabled={acting === u.id} variant="ghost" className="flex-1 border border-red-500/40 text-red-600 hover:bg-red-500/10 h-9 rounded-sm" data-testid={`reject-${u.id}`}>
                                            <XCircle className="h-4 w-4 mr-1.5" /> Weigeren
                                        </Button>
                                    </div>
                                )}
                                {u.approval_status === "rejected" && (
                                    <Button onClick={() => approve(u.id)} disabled={acting === u.id} className="bg-emerald-500/80 hover:bg-emerald-400 text-black font-bold h-9 rounded-sm">
                                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Alsnog goedkeuren
                                    </Button>
                                )}
                                {u.approval_status === "approved" && (
                                    <ReactRouterLink
                                        to={`/admin/users/${u.id}`}
                                        data-testid={`view-user-detail-${u.id}`}
                                        className="inline-flex items-center justify-center gap-1.5 h-8 px-3 text-[11px] bg-zinc-100 border border-zinc-200 text-zinc-950 hover:bg-zinc-100 rounded-sm transition"
                                    >
                                        <Coins className="h-3 w-3" /> Credits &amp; Tools
                                    </ReactRouterLink>
                                )}
                                {u.approval_status === "approved" && (
                                    <Button onClick={() => reject(u.id)} disabled={acting === u.id} variant="ghost" className="border border-zinc-200 text-zinc-500 hover:bg-zinc-100 h-9 rounded-sm text-xs">
                                        Toegang intrekken
                                    </Button>
                                )}
                                {u.approval_status === "approved" && (
                                    <button
                                        onClick={() => generateResetLink(u)}
                                        disabled={acting === u.id}
                                        className="inline-flex items-center justify-center gap-1.5 h-8 px-3 text-[11px] border border-[#E5A93D]/30 text-[#E5A93D] hover:bg-[#E5A93D]/10 rounded-sm transition active:scale-95"
                                        data-testid={`reset-pwd-btn-${u.id}`}
                                    >
                                        <KeyRound className="h-3 w-3" /> Reset wachtwoord
                                    </button>
                                )}
                                <button
                                    onClick={() => setDeleteFor(u)}
                                    className="inline-flex items-center justify-center gap-1.5 mt-1 text-[11px] text-red-400/80 hover:text-red-600 transition"
                                    data-testid={`delete-user-btn-${u.id}`}
                                >
                                    <Trash2 className="h-3 w-3" /> Verwijder account
                                </button>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>

            {/* Link-agents modal */}
            {linkingFor && (
                <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLinkingFor(null)} data-testid="link-agents-modal">
                    <div className="bg-[#FFFFFF] border border-[#7C3AED]/30 rounded-md max-w-md w-full max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6">
                            <div className="flex items-center gap-2 mb-2">
                                <Link2 className="h-4 w-4 text-[#7C3AED]" />
                                <h3 className="font-display font-bold text-xl">Makelaars koppelen</h3>
                            </div>
                            <p className="text-xs text-zinc-500 mb-5">Selecteer welke goedgekeurde makelaars deze marketing manager mag beheren.</p>
                            <div className="space-y-1 max-h-[45vh] overflow-y-auto">
                                {allAgents.length === 0 ? (
                                    <div className="text-zinc-500 text-sm py-6 text-center">Nog geen goedgekeurde makelaars</div>
                                ) : allAgents.map((a) => {
                                    const checked = linkSelection.includes(a.id);
                                    return (
                                        <label key={a.id} className={`flex items-center gap-3 p-3 rounded-sm cursor-pointer transition ${checked ? "bg-[#7C3AED]/10 border border-[#7C3AED]/30" : "bg-[#F4F4F5] border border-zinc-100 hover:border-zinc-400"}`} data-testid={`agent-option-${a.id}`}>
                                            <Checkbox
                                                checked={checked}
                                                onCheckedChange={(v) => {
                                                    if (v) setLinkSelection([...linkSelection, a.id]);
                                                    else setLinkSelection(linkSelection.filter((x) => x !== a.id));
                                                }}
                                                className="border-zinc-300 data-[state=checked]:bg-[#7C3AED] data-[state=checked]:border-[#7C3AED]"
                                            />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-zinc-950 truncate">{a.name}</div>
                                                <div className="text-xs text-zinc-500 truncate">{a.company || a.email}</div>
                                            </div>
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="flex gap-2 mt-6">
                                <Button onClick={() => setLinkingFor(null)} variant="ghost" className="flex-1 h-10 border border-zinc-200 text-zinc-600 rounded-sm">Annuleren</Button>
                                <Button onClick={saveLink} disabled={savingLink} className="flex-1 h-10 bg-[#7C3AED] hover:bg-[#8B5CF6] text-white font-bold rounded-sm" data-testid="save-link-agents">
                                    {savingLink ? "Bezig..." : "Opslaan"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Reset password link modal */}
            {resetLink && (
                <div className="fixed inset-0 z-[180] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setResetLink(null)} data-testid="reset-link-modal">
                    <div className="bg-[#FFFFFF] border border-[#E5A93D]/40 rounded-md max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-2">
                            <KeyRound className="h-5 w-5 text-[#E5A93D]" />
                            <h3 className="font-display font-bold text-xl">Wachtwoord reset link</h3>
                        </div>
                        <p className="text-sm text-zinc-500 mb-1">
                            Voor <span className="text-zinc-950 font-medium">{resetLink.user_name || resetLink.user_email}</span>
                        </p>
                        <p className="text-xs text-zinc-500 mb-5">
                            Deel deze eenmalig-geldige link via WhatsApp/SMS/in persoon. De gebruiker stelt zelf een nieuw wachtwoord in. Geldig {resetLink.ttl_hours}u.
                        </p>
                        <div className="bg-[#F4F4F5] border border-zinc-200 rounded-sm p-3 mb-4 break-all text-xs text-[#E5A93D] font-mono select-all" data-testid="reset-link-display">
                            {resetLink.url}
                        </div>
                        <div className="flex gap-2">
                            <Button onClick={() => setResetLink(null)} variant="ghost" className="flex-1 h-10 border border-zinc-200 text-zinc-600 rounded-sm">
                                Sluiten
                            </Button>
                            <Button
                                onClick={copyResetLink}
                                className="flex-1 h-10 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm active:scale-[0.98]"
                                data-testid="copy-reset-link-btn"
                            >
                                {resetCopied ? <><Check className="h-4 w-4 mr-1.5" /> Gekopieerd</> : <><Copy className="h-4 w-4 mr-1.5" /> Kopieer link</>}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            {/* Delete confirmation modal */}
            {deleteFor && (
                <div className="fixed inset-0 z-[200] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !deleting && setDeleteFor(null)} data-testid="delete-user-modal">
                    <div className="bg-[#FFFFFF] border border-red-500/30 rounded-md max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-3 text-red-600">
                            <Trash2 className="h-5 w-5" />
                            <h3 className="font-display font-bold text-xl">Account verwijderen?</h3>
                        </div>
                        <p className="text-sm text-zinc-600 mb-6">
                            <span className="font-bold text-zinc-950">{deleteFor.name}</span>
                            {deleteFor.company && <span className="text-zinc-500"> ({deleteFor.company})</span>}
                            {" "}wordt definitief verwijderd. Bestaande projecten van deze gebruiker blijven bewaard.
                        </p>
                        <div className="flex gap-2">
                            <button onClick={() => setDeleteFor(null)} disabled={deleting} className="flex-1 h-11 border border-zinc-200 text-zinc-600 hover:bg-zinc-100 rounded-sm text-sm">
                                Annuleren
                            </button>
                            <button
                                onClick={confirmDelete}
                                disabled={deleting}
                                className="flex-1 h-11 bg-red-500 hover:bg-red-400 disabled:opacity-50 text-zinc-950 font-bold rounded-sm text-sm active:scale-[0.98]"
                                data-testid="confirm-delete-user-btn"
                            >
                                {deleting ? "Bezig..." : "Verwijder"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Create user modal */}
            {createOpen && (
                <div className="fixed inset-0 z-[150] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => !creating && setCreateOpen(false)} data-testid="create-user-modal">
                    <div className="bg-[#FFFFFF] border border-[#E5A93D]/30 rounded-md max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6">
                            <div className="flex items-center gap-2 mb-2">
                                <UserPlus className="h-5 w-5 text-[#E5A93D]" />
                                <h3 className="font-display font-bold text-xl">Nieuwe {createForm.account_type === "marketing_manager" ? "marketing manager" : createForm.account_type === "photographer" ? "fotograaf" : "makelaar"}</h3>
                            </div>
                            <p className="text-xs text-zinc-500 mb-5">Account wordt automatisch goedgekeurd. Deel het paswoord met de gebruiker — hij/zij kan het later via het profiel aanpassen.</p>
                            <div className="space-y-3">
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Type account</label>
                                    <div className="flex gap-2">
                                        {ROLE_TABS.map((r) => (
                                            <button
                                                key={r.key}
                                                onClick={() => updateCreate("account_type", r.key)}
                                                className={`flex-1 py-2 text-xs font-bold rounded-sm border transition ${createForm.account_type === r.key ? "border-[#E5A93D] bg-[#E5A93D]/10 text-zinc-950" : "border-zinc-200 bg-[#F4F4F5] text-zinc-500 hover:border-zinc-400"}`}
                                                data-testid={`create-type-${r.key}`}
                                            >
                                                {r.label.replace("s", "")}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Naam *" testid="create-name" value={createForm.name} onChange={(v) => updateCreate("name", v)} placeholder="Jan Janssen" />
                                    <Field label="Bedrijf" testid="create-company" value={createForm.company} onChange={(v) => updateCreate("company", v)} placeholder="Immo Janssen" />
                                </div>
                                <Field label="E-mail *" testid="create-email" value={createForm.email} onChange={(v) => updateCreate("email", v)} placeholder="jan@immojanssen.be" type="email" />
                                <Field label="Telefoon" testid="create-phone" value={createForm.phone} onChange={(v) => updateCreate("phone", v)} placeholder="+32 ..." />
                                <Field label="Marketing e-mail (voor opleveringsmeldingen)" testid="create-marketing-email" value={createForm.marketing_email} onChange={(v) => updateCreate("marketing_email", v)} placeholder="marketing@..." type="email" />
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Paswoord *</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={createForm.password}
                                            onChange={(e) => updateCreate("password", e.target.value)}
                                            placeholder="min 6 tekens"
                                            className="flex-1 bg-[#F4F4F5] border border-zinc-200 rounded-sm px-3 py-2 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D]"
                                            data-testid="create-password"
                                        />
                                        <button onClick={generatePassword} type="button" className="px-3 py-2 text-xs bg-[#E5A93D]/15 border border-[#E5A93D]/30 text-[#E5A93D] rounded-sm hover:bg-[#E5A93D]/25" data-testid="create-generate-password">
                                            Genereer
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">Logo (optioneel)</label>
                                    <div className="flex items-center gap-3">
                                        {createForm.logo_url ? (
                                            <img src={createForm.logo_url.startsWith("http") ? createForm.logo_url : `${process.env.REACT_APP_BACKEND_URL}${createForm.logo_url}`} alt="" className="h-12 w-12 rounded-sm object-contain bg-zinc-100" />
                                        ) : (
                                            <div className="h-12 w-12 rounded-sm bg-zinc-100 border border-zinc-200 flex items-center justify-center text-zinc-400 text-xs">Geen</div>
                                        )}
                                        <label className="flex-1 px-3 py-2 text-xs text-center bg-[#F4F4F5] border border-dashed border-zinc-200 hover:border-[#E5A93D]/50 rounded-sm cursor-pointer text-zinc-500" data-testid="create-logo-upload">
                                            {logoUploading ? "Bezig..." : createForm.logo_url ? "Logo vervangen" : "Logo uploaden"}
                                            <input type="file" accept="image/*" className="hidden" onChange={onLogoUpload} />
                                        </label>
                                        {createForm.logo_url && (
                                            <button onClick={() => updateCreate("logo_url", "")} className="text-xs text-red-600">Wis</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 mt-6">
                                <Button onClick={() => setCreateOpen(false)} disabled={creating} variant="ghost" className="flex-1 h-11 border border-zinc-200 text-zinc-600 rounded-sm">Annuleren</Button>
                                <Button onClick={submitCreate} disabled={creating} className="flex-1 h-11 bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold rounded-sm" data-testid="create-user-submit">
                                    {creating ? "Bezig..." : "Account aanmaken"}
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, value, onChange, placeholder, type = "text", testid }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 block">{label}</label>
            <input
                type={type}
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full bg-[#F4F4F5] border border-zinc-200 rounded-sm px-3 py-2 text-sm text-zinc-950 focus:outline-none focus:border-[#E5A93D]"
                data-testid={testid}
            />
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        pending: { label: "In afwachting", cls: "bg-[#E5A93D]/15 text-[#E5A93D] border-[#E5A93D]/30", icon: Clock },
        approved: { label: "Goedgekeurd", cls: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30", icon: CheckCircle2 },
        rejected: { label: "Geweigerd", cls: "bg-red-500/15 text-red-600 border-red-500/30", icon: XCircle },
    };
    const s = map[status] || map.pending;
    const Icon = s.icon;
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border font-bold ${s.cls}`}>
            <Icon className="h-3 w-3" />
            {s.label}
        </span>
    );
}
